import * as React from 'react'
import ReactDropzone from 'react-dropzone'

import { Icon, Text } from '@factorialco/deprecated-design-system'
import classnames from 'classnames'

import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'

import styles from './index.module.scss'

type DropzoneProps = React.ComponentProps<typeof ReactDropzone>

interface Props {
  disabled?: boolean
  onDrop: DropzoneProps['onDrop']
  multiple?: boolean
  accept?: DropzoneProps['accept']
  subtitle?: string
  loading?: boolean
  placeholder?: string
}

// MB to Byte
const MAX_FILE_SIZE = 200 * 1024 ** 2

const DropZone = (props: Props) => {
  const attrs = props.accept
    ? {
        accept: props.accept,
      }
    : {}

  const [rejectedFiles, setRejectedFiles] = React.useState(new Array<File>())

  const validateFiles: DropzoneProps['onDrop'] = (accepted, rejected, event) => {
    setRejectedFiles(rejected.map((rejection) => rejection.file))

    if (rejected.length === 0 && props.onDrop) {
      props.onDrop(accepted, [], event)
    }
  }

  const hasCustomAcceptedFiles = attrs.accept !== undefined
  const placeholder = props.placeholder || i18n.t('file_importer.default_placeholder')
  const subtitle = props.subtitle

  const primaryColor = props.disabled ? 'grey600' : 'grey700'

  return (
    <ReactDropzone
      onDrop={validateFiles}
      multiple={props.multiple}
      disabled={props.disabled}
      maxSize={MAX_FILE_SIZE}
      {...attrs}
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <div
          {...getRootProps()}
          className={classnames(styles.box, {
            [styles.loading!]: props.loading,
            [styles.boxActive!]: isDragActive,
            [styles.disabled!]: props.disabled,
          })}
        >
          <input {...getInputProps({ 'aria-label': placeholder })} />
          {props.loading && <Loading inline />}
          {!props.loading && (
            <>
              <Icon.Large icon="file-upload" color={primaryColor} />
              <div className={styles.text}>{placeholder}</div>
              {subtitle && (
                <Text color={primaryColor} weight="medium" size="100">
                  {subtitle}
                </Text>
              )}
              {rejectedFiles.length > 0 && (
                <Text size="300" color="radicalRed1200" htmlTag="div">
                  {hasCustomAcceptedFiles
                    ? i18n.t('file_importer.unsupported_file_format_or_file_size_exceeded')
                    : i18n.t('file_importer.file_size_exceeded')}
                  <ul>
                    {rejectedFiles.map((f) => (
                      <li key={f.name}>{f.name}</li>
                    ))}
                  </ul>
                </Text>
              )}
            </>
          )}
        </div>
      )}
    </ReactDropzone>
  )
}

export default DropZone
